import { useEffect, useRef, useState } from 'react'
import { ApiError, shortDate } from '../lib/api'
import { legalApi, OUTCOME_WORD, VERDICT_WORD, type ClauseVerdict, type LegalOutcome, type LegalReviews } from '../lib/legal'
import { personLabel, usePeopleNames } from '../lib/people'
import { ErrorBox, Loading } from './DataTable'
import { Status, Toggletip, type Tone } from './kit'

// Legal counsel's review of one agreement (release 3.1, package T3): clause by
// clause, then the agreement as a whole, with a note the next reviewer and an
// auditor read. The form is offered only to somebody the API would accept it
// from (mayReview); everybody who can read the document reads the history.

const OUTCOME_TONE: Record<string, Tone> = { ACCEPTABLE: 'ok', CHANGES_REQUIRED: 'warn', NOT_ACCEPTABLE: 'crit' }

export function outcomeStatus(outcome: string | undefined) {
  if (!outcome) return <span className="muted">Not reviewed</span>
  return <Status tone={OUTCOME_TONE[outcome] ?? 'neutral'}>{OUTCOME_WORD[outcome] ?? outcome}</Status>
}

export function LegalReviewPanel({ documentId, title, onClose, onSaved }: { documentId: string; title: string; onClose: () => void; onSaved: () => void }) {
  const [data, setData] = useState<LegalReviews | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [verdicts, setVerdicts] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [outcome, setOutcome] = useState<LegalOutcome | ''>('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [saved, setSaved] = useState('')
  const names = usePeopleNames()
  const ref = useRef<HTMLElement | null>(null)

  function load() {
    legalApi
      .reviews(documentId)
      .then(setData)
      .catch(setError)
  }
  useEffect(() => {
    setData(null)
    setError(null)
    setVerdicts({})
    setNotes({})
    setOutcome('')
    setNote('')
    setSaved('')
    setSaveError(null)
    load()
    ref.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId])

  async function save() {
    if (!data || outcome === '') return
    const clauses: ClauseVerdict[] = data.clauses
      .filter((c) => verdicts[c.code])
      .map((c) => ({ code: c.code, verdict: verdicts[c.code] as ClauseVerdict['verdict'], note: notes[c.code]?.trim() || undefined }))
    setSaving(true)
    setSaveError(null)
    try {
      await legalApi.record(documentId, { outcome, clauses, note: note.trim() })
      setSaved('Legal review recorded: ' + (OUTCOME_WORD[outcome] ?? outcome) + '.')
      setVerdicts({})
      setNotes({})
      setOutcome('')
      setNote('')
      load()
      onSaved()
    } catch (e) {
      setSaveError(e instanceof ApiError ? e : new ApiError(0, 'UNKNOWN', String(e)))
    } finally {
      setSaving(false)
    }
  }

  const issueFor = (field: string) => saveError?.issues.find((i) => i.field === field)?.detail

  return (
    <section className="panel" aria-labelledby="legal-review-heading" ref={ref} tabIndex={-1} data-testid="legal-review" style={{ marginTop: 16 }}>
      <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 id="legal-review-heading" style={{ margin: 0 }}>
          Legal review of {title}
        </h2>
        <button type="button" className="ghost" onClick={onClose}>
          Close
        </button>
      </div>
      <div style={{ padding: '12px 16px' }}>
        {error !== null && <ErrorBox error={error} what="The legal reviews" />}
        {data === null && error === null && <Loading what="the legal reviews" />}
        {data && !data.reviewable && <p className="muted">This is not an agreement. A legal review is for a DPA, MSA, SOW or NDA.</p>}
        {saved !== '' && (
          <div className="ok-note" role="status">
            <p style={{ margin: 0 }}>{saved}</p>
          </div>
        )}

        {data && data.reviewable && data.mayReview && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void save()
            }}
          >
            {saveError !== null && <ErrorBox error={saveError} what="The legal review" verb="recorded" />}
            <table className="table">
              <caption className="vh">The clauses of this agreement and your verdict on each. Clauses left as not reviewed are not recorded.</caption>
              <thead>
                <tr>
                  <th>Clause</th>
                  <th>Verdict</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.clauses.map((c) => (
                  <tr key={c.code}>
                    <td>
                      {c.name} <Toggletip label={'Why ' + c.name + ' matters'}>{c.why}</Toggletip>
                    </td>
                    <td>
                      <select
                        aria-label={'Verdict on ' + c.name}
                        value={verdicts[c.code] ?? ''}
                        onChange={(e) => setVerdicts((v) => ({ ...v, [c.code]: e.target.value }))}
                      >
                        <option value="">Not reviewed</option>
                        <option value="ADEQUATE">Adequate</option>
                        <option value="INADEQUATE">Inadequate</option>
                        <option value="MISSING">Missing</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        aria-label={'Note on ' + c.name}
                        value={notes[c.code] ?? ''}
                        disabled={!verdicts[c.code]}
                        onChange={(e) => setNotes((n) => ({ ...n, [c.code]: e.target.value }))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {issueFor('clauses') && <p className="field-error">{issueFor('clauses')}</p>}
            <fieldset style={{ marginTop: 12 }}>
              <legend>The agreement as a whole</legend>
              {data.outcomes.map((o) => (
                <label key={o} style={{ marginRight: 16 }}>
                  <input type="radio" name="legal-outcome" value={o} checked={outcome === o} onChange={() => setOutcome(o)} /> {OUTCOME_WORD[o] ?? o}
                </label>
              ))}
            </fieldset>
            {issueFor('outcome') && <p className="field-error">{issueFor('outcome')}</p>}
            <div className="field" style={{ marginTop: 10 }}>
              <label htmlFor="legal-note">Why</label>
              <textarea id="legal-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
              {issueFor('note') && <p className="field-error">{issueFor('note')}</p>}
            </div>
            <div className="commit-bar">
              <button type="submit" className="primary" data-action="tprm.documents.a02" disabled={saving || outcome === '' || note.trim() === ''}>
                {saving ? 'Recording' : 'Record the legal review'}
              </button>
              <span className="muted">Recorded on the evidence chain; a later review supersedes it and both are kept</span>
            </div>
          </form>
        )}

        {data && data.reviews.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ margin: '0 0 6px' }}>Reviews</h3>
            <ul className="plain-list">
              {data.reviews.map((r) => (
                <li key={r.id} style={{ marginBottom: 10 }}>
                  <span className="chiprow">
                    {outcomeStatus(r.outcome)}
                    {r.current ? <Status tone="info">Standing</Status> : <Status tone="neutral">Superseded</Status>}
                    <span className="muted">
                      {personLabel(names, r.reviewedBy)}, {shortDate(r.reviewedAt)}
                    </span>
                  </span>
                  <p style={{ margin: '4px 0' }}>{r.note}</p>
                  {r.clauses.length > 0 && (
                    <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                      {r.clauses
                        .map((c) => (data.clauses.find((x) => x.code === c.code)?.name ?? c.code) + ': ' + (VERDICT_WORD[c.verdict] ?? c.verdict) + (c.note ? ' (' + c.note + ')' : ''))
                        .join('; ')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {data && data.reviewable && data.reviews.length === 0 && !data.mayReview && (
          <p className="muted">Nobody has recorded a legal review of this agreement yet.</p>
        )}
      </div>
    </section>
  )
}
